import { defineStore } from "pinia";
import type {
  WebModelGetUserAccessToken,
  WebModelPostNewUserAccessToken,
  WebModelPutUserAccessToken,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import { withErrorHandling } from "~/helpers/withErrorHandling";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { withLoading } from "~/helpers/withLoading";
import {
  createNewExternalUserAccessToken,
  deleteUserAccessToken,
  getExternalUserAccessToken,
  updateUserAccessToken,
} from "~/model/api/pLCnextStoreFrontendAPI";
import { useErrorMessage } from "~/composables/useErrorMessage";
import { AxiosError } from "axios";

interface State {
  userToken: WebModelGetUserAccessToken | null;
}

export const useUserAccessTokenStore = defineStore("userAccessToken", {
  state: (): State => ({
    userToken: null, // initial state
  }),
  getters: {},
  actions: {
    async loadUserAPIAccessToken() {
      const { data, error } = await withErrorHandling(
        withLoading(getExternalUserAccessToken()),
      );
      console.debug("loadUserAccessToken: ", data, error);
      const errorMessageHandler = useErrorMessage();

      // in case its just a 404 not found we dont want the error message showing
      if (error) {
        // if error instance of AxiosError and error is status 404 - ignore
        if (error instanceof AxiosError && error.response?.status === 404) {
          // ignored
        } else {
          // pass to error message handler
          errorMessageHandler(error);
        }
      }

      if (data) {
        this.userToken = data;
      } else {
        this.userToken = null;
      }
    },

    async addUserAPIAccessToken(description?: string, expirationDate?: string) {
      const webModel: WebModelPostNewUserAccessToken = {
        description: description,
        expirationDate: expirationDate,
      };
      const { data } = await withErrorMessage(
        withLoading(createNewExternalUserAccessToken(webModel)),
      );
      await this.loadUserAPIAccessToken();
      if (data) {
        return data;
      }
      return null;
    },

    async deleteUserAPIAccessToken() {
      const { error } = await withErrorHandling(
        withErrorMessage(withLoading(deleteUserAccessToken())),
      );
      console.debug("deleteUserAPIAccessToken: ", error);
      if (!error) {
        await this.loadUserAPIAccessToken();
        return true;
      } else return false;
    },

    async updateUserAPIAccessToken(
      description?: string,
      expirationDate?: string,
    ) {
      const webModel: WebModelPutUserAccessToken = {
        description: description,
        expirationDate: expirationDate,
      };
      await withErrorMessage(withLoading(updateUserAccessToken(webModel)));
      console.debug("updateUserAPIAccessToken: ");
      await this.loadUserAPIAccessToken();
    },

    clear() {
      this.$reset();
    },
  },
});
